import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, Clock, Plus, ShoppingBag, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import MobileNav from '../components/MobileNav';
import CartDrawer from '../components/CartDrawer';
import TruckLocationMap from '../components/TruckLocationMap';
import { directionsUrl, hasExactLocation, withTruckDistance } from '../lib/truckLocation';
import useCustomerLocation from '../hooks/useCustomerLocation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TruckDetail() {
  const { truckId } = useParams();
  const navigate = useNavigate();
  const { user, openSignIn } = useAuth();
  const { cart, addItem, getItemCount } = useCart();
  const [truck, setTruck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cartOpen, setCartOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [ratingSummary, setRatingSummary] = useState({ rating_avg: 0, rating_count: 0 });
  const { coords: customerCoords } = useCustomerLocation();

  const handleViewCart = () => {
    if (!user) {
      toast.info('Please sign in to place an order');
      openSignIn();
      return;
    }
    setCartOpen(false);
    navigate('/checkout');
  };

  useEffect(() => {
    const fetchTruck = async () => {
      try {
        const response = await fetch(`${API_URL}/api/trucks/${truckId}`);
        if (!response.ok) throw new Error('Truck not found');
        const data = await response.json();
        setTruck(data);
      } catch (error) {
        console.error('Failed to fetch truck:', error);
        toast.error('Failed to load food truck');
      } finally {
        setLoading(false);
      }
    };
    fetchTruck();
  }, [truckId]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch(`${API_URL}/api/trucks/${truckId}/reviews`);
        if (response.ok) {
          const data = await response.json();
          setReviews(data.reviews || []);
          setRatingSummary({
            rating_avg: data.rating_avg || 0,
            rating_count: data.rating_count || 0,
          });
        }
      } catch (error) {
        console.error('Failed to load reviews:', error);
      }
    };
    fetchReviews();
  }, [truckId]);

  const formatReviewDate = (isoString) => {
    try {
      return new Date(isoString).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
      });
    } catch {
      return '';
    }
  };

  const handleAddToCart = (item) => {
    if (!user) {
      toast.info('Please sign in to place an order');
      openSignIn();
      return;
    }
    addItem(item, truck);
    toast.success(`Added ${item.name} to cart`);
  };

  const getTodayHours = () => {
    if (!truck?.operating_hours) return null;
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = days[new Date().getDay()];
    return truck.operating_hours[today];
  };

  const todayHours = getTodayHours();

  // Group menu items by category
  const groupedItems = truck?.menu_items?.reduce((acc, item) => {
    const category = item.category || 'Menu';
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {}) || {};

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-3xl mx-auto px-4 pt-20 animate-pulse">
          <div className="aspect-video bg-muted rounded-2xl mb-6" />
          <div className="h-8 bg-muted rounded w-1/2 mb-4" />
          <div className="h-4 bg-muted rounded w-3/4" />
        </div>
      </div>
    );
  }

  if (!truck) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-3xl mx-auto px-4 pt-20 text-center">
          <h1 className="text-2xl font-bold mb-4">Truck not found</h1>
          <Button onClick={() => navigate('/')}>Back to Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-safe">
      <Header />
      
      {/* Hero Image */}
      <div className="relative h-64 sm:h-80">
        <img 
          src={truck.photo_url || 'https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=800'} 
          alt={truck.name}
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
        <button 
          onClick={() => navigate(-1)}
          className="absolute top-20 left-4 w-10 h-10 rounded-full bg-background/95 flex items-center justify-center hover:bg-background transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </div>

      {/* Truck Info */}
      <div className="max-w-3xl mx-auto px-4 -mt-16 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="bg-card rounded-2xl shadow-lg p-6 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <Badge variant="secondary" className="mb-2">
                  {truck.cuisine_type || 'Food Truck'}
                </Badge>
                <h1 className="text-2xl font-bold" data-testid="truck-name">{truck.name}</h1>
                {ratingSummary.rating_count > 0 ? (
                  <div className="flex items-center gap-1 mt-1 text-sm" data-testid="truck-rating">
                    <Star className="w-4 h-4 fill-primary text-primary" />
                    <span className="font-medium">{Number(ratingSummary.rating_avg).toFixed(1)}</span>
                    <span className="text-muted-foreground">
                      ({ratingSummary.rating_count} review{ratingSummary.rating_count > 1 ? 's' : ''})
                    </span>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1">No reviews yet</p>
                )}
              </div>
            </div>
            
            <p className="text-muted-foreground mb-4">{truck.description}</p>
            
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span>{truck.location}</span>
                {customerCoords && withTruckDistance(truck, customerCoords).distance_label && (
                  <span className="text-primary font-medium" data-testid="truck-detail-distance">
                    {withTruckDistance(truck, customerCoords).distance_label}
                  </span>
                )}
                <a
                  href={hasExactLocation(truck.exact_location)
                    ? directionsUrl(truck.exact_location)
                    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(truck.location || '')}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline text-xs"
                >
                  Open in Maps
                </a>
              </div>
              {todayHours && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  <span>{todayHours.open} - {todayHours.close}</span>
                </div>
              )}
            </div>

            {hasExactLocation(truck.exact_location) && (
              <div className="mt-4 space-y-2">
                {truck.exact_location.note && (
                  <p className="text-sm" data-testid="truck-location-note">
                    <span className="font-medium">Exact spot:</span> {truck.exact_location.note}
                  </p>
                )}
                <TruckLocationMap location={truck.exact_location} />
              </div>
            )}
          </div>

          {/* Menu */}
          <h2 className="text-xl font-bold mb-4">Menu</h2>
          
          {Object.entries(groupedItems).map(([category, items]) => (
            <div key={category} className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-muted-foreground">{category}</h3>
              <div className="space-y-4">
                {items.map((item, i) => (
                  <motion.div
                    key={item.item_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    data-testid={`menu-item-${item.item_id}`}
                    className="bg-card rounded-xl overflow-hidden shadow-sm border border-black/5"
                  >
                    <div className="flex">
                      {item.photo_url && (
                        <div className="w-28 h-28 flex-shrink-0">
                          <img 
                            src={item.photo_url} 
                            alt={item.name}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <div className="flex-1 p-4 flex flex-col justify-between">
                        <div>
                          <div className="flex items-start justify-between">
                            <h4 className="font-medium">{item.name}</h4>
                            {!item.available && (
                              <Badge variant="secondary" className="text-xs">Sold Out</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                            {item.description}
                          </p>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <span className="font-semibold text-primary">
                            CA$ {item.price.toFixed(2)}
                          </span>
                          {item.available && (
                            <Button 
                              size="sm" 
                              className="rounded-full h-8 px-4"
                              onClick={() => handleAddToCart(item)}
                              data-testid={`add-to-cart-${item.item_id}`}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Add
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}

          {/* Reviews */}
          <div className="mb-10">
            <h2 className="text-xl font-bold mb-4">Reviews</h2>
            {reviews.length === 0 ? (
              <p className="text-sm text-muted-foreground">No reviews yet. Be the first after your next order!</p>
            ) : (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div key={review.review_id} className="bg-card rounded-xl p-4 shadow-sm border border-black/5">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{review.student_name || 'Student'}</span>
                      <span className="text-xs text-muted-foreground">{formatReviewDate(review.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-0.5 mb-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-4 h-4 ${review.rating >= star ? 'fill-primary text-primary' : 'text-muted-foreground'}`}
                        />
                      ))}
                    </div>
                    {review.review_text && (
                      <p className="text-sm text-muted-foreground">{review.review_text}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Floating Cart Button */}
      {getItemCount() > 0 && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="fixed left-4 right-4 max-w-3xl mx-auto z-[70]"
          style={{ bottom: '5rem' }}
        >
          <Button 
            size="lg" 
            className="w-full rounded-full shadow-lg"
            onClick={handleViewCart}
            data-testid="view-cart-btn"
          >
            <ShoppingBag className="w-5 h-5 mr-2" />
            View Cart ({getItemCount()})
          </Button>
        </motion.div>
      )}

      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
      <MobileNav />
    </div>
  );
}
